import { useState } from 'react';
import {
  DEFAULT_SCOPE, type Frequency, PRIORITIES, type Priority, type Routine, WEEKDAYS_LONG, WEEKDAYS_SHORT, addDays, describeFrequency,
  fmtShort, localToday, nextOccurrences, parseISODate, plural, routineInput,
} from '@quadro/domain';
import { confirmAction } from '../../components/Confirm';
import { hud } from '../../components/Hud';
import { Icon } from '../../components/Icon';
import { Options } from '../../components/Options';
import { Sheet, SheetFooter, useAutoFocus } from '../../components/Sheet';
import * as api from '../../data/api';
import { keys, useAction, useRoutines } from '../../data/queries';
import { closeSheet } from '../../state/sheets';
import { useUI } from '../../state/ui';
import { useWorkspace } from '../../state/workspace';
import { EntityField } from '../orgmap/EntityField';
import { Automations } from './Automations';
import { RoutineProcesses } from './RoutineProcesses';
import { useHolidayCheck } from './RoutinesPage';

const UNITS: Record<Frequency['type'], [string, string]> = { daily: ['dia', 'dias'], weekly: ['semana', 'semanas'], monthly: ['mês', 'meses'], yearly: ['ano', 'anos'] };

function defaultFrequency(type: Frequency['type'], interval: number, today: string): Frequency {
  const d = parseISODate(today);
  if (type === 'daily') return { type, interval, businessDaysOnly: true };
  if (type === 'weekly') return { type, interval, weekdays: [d.getDay() || 1] };
  if (type === 'monthly') return { type, interval, dayOfMonth: d.getDate() };
  return { type, interval };
}

export function RoutineSheet({ id }: { id: string | null }) {
  const ws = useWorkspace();
  const routines = useRoutines().data;
  const existing = id ? routines?.find(r => r.id === id) : null;
  if (id && !existing) return null;
  return <RoutineForm key={id ?? 'new'} existing={existing ?? null} canWrite={ws.canWrite} />;
}

function RoutineForm({ existing, canWrite: ed }: { existing: Routine | null; canWrite: boolean }) {
  const ws = useWorkspace();
  const today = localToday();
  const scope = useUI(s => s.scope) ?? DEFAULT_SCOPE;
  const titleRef = useAutoFocus<HTMLInputElement>();
  const [r, setR] = useState<api.RoutineDraft>(() => existing ? structuredClone(existing) : {
    title: '', description: '', projectId: scope.project !== 'all' && scope.project !== 'mine' ? scope.project : null, assigneeId: ws.me.id,
    priority: 0, themeIds: [], entityIds: [], classification: null, startStatus: 'todo',
    frequency: defaultFrequency('weekly', 1, today), startsOn: today, leadDays: 0, checklist: [], active: true,
  });
  const [checklist, setChecklist] = useState((existing?.checklist ?? []).join('\n'));
  const patch = (p: Partial<api.RoutineDraft>) => setR(x => ({ ...x, ...p }));
  const patchFreq = (p: Partial<Frequency>) => setR(x => ({ ...x, frequency: { ...x.frequency, ...p } as Frequency }));
  const save = useAction(api.saveRoutine, [keys.routines, keys.tasks]);
  const remove = useAction(api.deleteRoutine, [keys.routines]);
  const isHoliday = useHolidayCheck(today, addDays(today, 800));
  const category = ws.settings.categories.find(c => c.id === r.classification?.categoryId);
  const people = [...new Set([...ws.members.map(p => p.id), r.assigneeId].filter(Boolean) as string[])].map(i => [i, ws.person(i).name] as [string, string]);
  const f = r.frequency;
  const next = nextOccurrences({ ...r, active: true }, today, 4, isHoliday);

  async function submit() {
    const draft = { ...r, title: r.title.trim(), checklist: checklist.split('\n').map(x => x.trim()).filter(Boolean) };
    const v = routineInput.safeParse(draft);
    if (!v.success) { hud(v.error.issues[0]?.message ?? 'Confira os campos.'); return; }
    await save(existing?.id ?? null, draft);
    closeSheet(); hud(existing ? 'Rotina salva' : 'Rotina criada');
  }

  return (
    <Sheet wide onClose={closeSheet} label="Rotina">
      <div className="sh-body">
        <p className="rec-tag"><Icon name="rec" /> {existing ? 'Rotina' : 'Nova rotina'}</p>
        <input className="big-title" ref={titleRef} placeholder="Ex.: Fechamento do relatório semanal" aria-label="Nome da rotina" maxLength={140} disabled={!ed} value={r.title} onChange={e => patch({ title: e.target.value })} />
        <fieldset disabled={!ed} style={{ border: 0, padding: 0, margin: 0 }}>
          <div className="form two">
            <label htmlFor="rProj">Projeto</label>
            <select id="rProj" value={r.projectId ?? ''} onChange={e => patch({ projectId: e.target.value || null })}><Options items={[['', 'Sem projeto'], ...ws.settings.projects.map(p => [p.id, p.name] as [string, string])]} /></select>
            <label htmlFor="rResp">Responsável</label>
            <select id="rResp" value={r.assigneeId ?? ''} onChange={e => patch({ assigneeId: e.target.value || null })}><Options items={[['', 'Ninguém'], ...people]} /></select>
            <label htmlFor="rPrio">Prioridade</label>
            <select id="rPrio" value={r.priority} onChange={e => patch({ priority: Number(e.target.value) as Priority })}><Options items={PRIORITIES.map((p, i) => [i, p])} /></select>
            <label htmlFor="rEtapa">Entra em</label>
            <select id="rEtapa" value={r.startStatus} onChange={e => patch({ startStatus: e.target.value as Routine['startStatus'] })}><Options items={[['mapped', 'Mapeado'], ['todo', 'A fazer'], ['doing', 'Fazendo']]} /></select>
            {ws.settings.categories.length > 0 && <>
              <label htmlFor="rCat">Categoria</label>
              <select id="rCat" value={r.classification?.categoryId ?? ''} onChange={e => patch({ classification: e.target.value ? { categoryId: e.target.value, subcategoryId: null } : null })}>
                <Options items={[['', 'Não classificada'], ...ws.settings.categories.map(c => [c.id, c.name] as [string, string])]} />
              </select>
              <label htmlFor="rSub">Subcategoria</label>
              <select id="rSub" disabled={!category} value={r.classification?.subcategoryId ?? ''} onChange={e => patch({ classification: { categoryId: r.classification!.categoryId, subcategoryId: e.target.value || null } })}>
                <Options items={[['', category ? 'Nenhuma' : '—'], ...(category?.subcategories ?? []).map(s => [s.id, s.name] as [string, string])]} />
              </select>
            </>}
            {ws.settings.themes.length > 0 && <>
              <label>Temas</label>
              <div className="full tema-picker">{ws.settings.themes.map(t => (
                <button key={t.id} type="button" className="tchip pick" style={{ '--tc': t.color } as React.CSSProperties} aria-pressed={r.themeIds.includes(t.id)}
                  onClick={() => patch({ themeIds: r.themeIds.includes(t.id) ? r.themeIds.filter(x => x !== t.id) : [...r.themeIds, t.id] })}>{t.name}</button>
              ))}</div>
            </>}
            <label>Entidades</label>
            <div className="full ent-field"><EntityField value={r.entityIds} onChange={ids => patch({ entityIds: ids })} editable={ed} /></div>
          </div>

          <h3>Recorrência</h3>
          <div className="seg freq-tipos" role="group" aria-label="Frequência">
            {([['daily', 'Diária'], ['weekly', 'Semanal'], ['monthly', 'Mensal'], ['yearly', 'Anual']] as const).map(([k, v]) => (
              <button key={k} type="button" aria-pressed={f.type === k} onClick={() => f.type !== k && patch({ frequency: defaultFrequency(k, f.interval, r.startsOn) })}>{v}</button>
            ))}
          </div>
          <div className="freq-row">
            <label className="inl">A cada <input type="number" className="inline-n" min={1} max={52} value={f.interval} onChange={e => patchFreq({ interval: Math.max(1, Number(e.target.value) || 1) })} /> <span>{f.interval > 1 ? UNITS[f.type][1] : UNITS[f.type][0]}</span></label>
            {f.type === 'daily' && <label className="switch"><input type="checkbox" checked={f.businessDaysOnly} onChange={e => patchFreq({ businessDaysOnly: e.target.checked })} /> Só dias úteis</label>}
            {f.type === 'weekly' && (
              <div className="wd-pick" role="group" aria-label="Dias da semana">{WEEKDAYS_SHORT.map((d, i) => (
                <button key={i} type="button" aria-pressed={f.weekdays.includes(i)} aria-label={WEEKDAYS_LONG[i]}
                  onClick={() => patchFreq({ weekdays: f.weekdays.includes(i) ? f.weekdays.filter(x => x !== i) : [...f.weekdays, i] })}>{d[0]}</button>
              ))}</div>
            )}
            {f.type === 'monthly' && (
              <label className="inl">no <select value={String(f.dayOfMonth)} onChange={e => patchFreq({ dayOfMonth: e.target.value === 'last' ? 'last' : Number(e.target.value) })}>
                <Options items={[...[...Array(31)].map((_, i) => [i + 1, `dia ${i + 1}`] as [number, string]), ['last', 'último dia']]} />
              </select></label>
            )}
            {f.type === 'yearly' && <span className="muted">Na data de início, todo ano.</span>}
          </div>
          <div className="form two" style={{ marginTop: 12 }}>
            <label htmlFor="rIni">Começa em</label>
            <input type="date" id="rIni" value={r.startsOn} onChange={e => patch({ startsOn: e.target.value || today })} />
            <label htmlFor="rAnt">Criar a tarefa</label>
            <select id="rAnt" value={r.leadDays} onChange={e => patch({ leadDays: Number(e.target.value) })}>
              <Options items={[[0, 'No próprio dia'], [1, '1 dia antes'], [2, '2 dias antes'], [3, '3 dias antes'], [5, '5 dias antes'], [7, '1 semana antes'], [14, '2 semanas antes']]} />
            </select>
          </div>
          <p className="rprev"><b>{describeFrequency(f, r.startsOn)}.</b> {next.length ? `Próximas: ${next.map(fmtShort).join(', ')}.` : 'Nenhuma ocorrência com essas regras.'}{r.leadDays ? ` A tarefa aparece no quadro ${plural(r.leadDays, 'dia', 'dias')} antes.` : ''}</p>
          <h3>Subtarefas padrão</h3>
          <textarea className="plain-area" rows={3} aria-label="Subtarefas padrão" value={checklist} onChange={e => setChecklist(e.target.value)}
            placeholder={'Uma por linha. Ex.:\nBaixar os números\nRevisar com a liderança\nPublicar no canal'} />
          {existing && <label className="switch" style={{ marginTop: 14 }}><input type="checkbox" checked={r.active} onChange={e => patch({ active: e.target.checked })} /> Rotina ativa</label>}
        </fieldset>
        {existing && <RoutineProcesses routineId={existing.id} />}
        {existing
          ? <Automations routineId={existing.id} canWrite={ed} />
          : <p className="muted" style={{ marginTop: 14 }}>Depois de criar a rotina, dá para adicionar automações: e-mails de cobrança e o relatório dos fundos.</p>}
      </div>
      <SheetFooter>
        {existing && ed && <button className="btn link" onClick={() => confirmAction({
          title: 'Excluir esta rotina?', text: `“${existing.title}” deixa de gerar tarefas. As tarefas já criadas continuam no quadro.`, action: 'Excluir',
          onConfirm: async () => { closeSheet(); await remove(existing.id); hud('Rotina excluída'); },
        })}>Excluir rotina</button>}
        <span className="grow" />
        <button className="btn" onClick={closeSheet}>{ed ? 'Cancelar' : 'Fechar'}</button>
        {ed && <button className="btn primary" disabled={!r.title.trim()} onClick={submit}>{existing ? 'Salvar' : 'Criar rotina'}</button>}
      </SheetFooter>
    </Sheet>
  );
}
